import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import type { MutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic/types';

/**
 * Stable `{ value }` bag for frame-loop / R3F state.
 * Mutations do not re-render. Prefer this over `useMemo` or `useRef().current`
 * (the latter is banned during render by React Compiler).
 *
 * WHY useState AND NOT useMemo
 *
 * This used to be `useMemo(() => ({ value: init() }), [init])`, which was
 * not stable at all. Every call site passes an inline arrow function, so
 * `init` is a new identity on every render, the memo invalidates, and the
 * hook hands back a BRAND NEW bag with `init()` run again.
 *
 * That broke in two ways at once, both silent:
 *
 *   - Any value written into the bag was reset to its initial on the next
 *     render of the owning component. Animation progress driven from
 *     motion's animate() simply never accumulated.
 *   - A writer that captured the bag once (`useRef(bag)`, the pattern this
 *     codebase uses to satisfy react-hooks/immutability) kept writing to
 *     the FIRST bag, while readers received the current one. Writer and
 *     reader were permanently pointed at different objects.
 *
 * useState's initialiser is the right primitive here: React guarantees it
 * runs exactly once for the life of the component and never re-runs it.
 * `useMemo` carries no such guarantee even with an empty dependency array
 * -- React is explicitly allowed to discard memo caches -- so it is the
 * wrong tool for something whose identity must survive.
 */
export function useMutableRef(init) {
  const $ = _c(2);
  let t0;
  if ($[0] !== init) {
    t0 = () => ({
      value: init()
    });
    $[0] = init;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [bag] = useState(t0);
  return bag;
}